import React from 'react';
import {
  Card, Grid, CardContent, CardMedia, Typography, Box
} from '@mui/material';
import { motion } from 'framer-motion';
import config from '../config';
const url = config.API_URL;

/*
 * SectionCard
 * Component used to display a titled section with optional image and icon.
 *
 * Props:
 * - title: string - heading text for the section
 * - image: string (optional) - URL to an image; if provided the layout will
 *   include an image column beside the content on md+ screens
 * - icon: React node (optional) - small icon displayed inline next to title
 * - children: React node - content body (paragraphs, lists, etc.)
 * - invert: boolean - when true and an image exists, the image is placed on
 *   the right (row-reverse) instead of the default left side
 * - isFirst: boolean (optional) - if true, the image will be eager-loaded with high priority for LCP optimization
 */
const SectionCard = ({ title, image, icon, children, invert = false, cardSx = {}, contentSx = {}, isFirst = false }) => {
  
  // used to decide layout direction
  const hasImage = Boolean(image);
  const layoutDirection = hasImage ? (invert ? 'row-reverse' : 'row') : 'column';

  let resolvedImage = image
    ? (typeof image === 'string' && (image.startsWith('http://') || image.startsWith('https://'))
        ? image
        : url + (image.startsWith('/') ? image : '/' + image))
    : null;

  // Optimize images from Cloudinary
  if (resolvedImage && resolvedImage.includes('cloudinary.com')) {
    resolvedImage = resolvedImage.replace('/upload/', '/upload/f_auto,q_auto,w_800,c_limit/');
  }

  return (
    // Subtle entrance animation when the component scrolls into view.
    // Use viewport={{ once: true }} to prevent re-animation on scroll
    // Use will-change for GPU acceleration
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.5 }}
      style={{ minHeight: 'fit-content', willChange: 'opacity, transform' }}
    >
      <Card
        sx={{
          backgroundColor: '#121212',
          color: '#fff',
          borderRadius: 3,
          boxShadow: 4,
          overflow: 'hidden',
          my: 4,
          px: { xs: 1, md: 3 },
          py: 2,
          // allow callers to override card-level sx to adjust padding on mobile)
          ...cardSx
        }}
      >
        <Grid container spacing={4} direction={layoutDirection} alignItems="center">
          {hasImage && (
            <Grid item xs={12} md={5}>
              <CardMedia
                component="img"
                image={resolvedImage}
                alt={title}
                loading={isFirst ? "eager" : "lazy"}
                fetchPriority={isFirst ? "high" : undefined}
                width="800"
                height="600"
                sx={{ width: '100%', height: 'auto', borderRadius: 2, objectFit: 'cover' }}
              />
            </Grid>
          )}

          <Grid item xs={12} md={hasImage ? 7 : 12}>
            <CardContent sx={{ px: { xs: 2, md: 3 }, py: 1, ...contentSx }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                {icon}
                <Typography variant="h5" sx={{ ml: 2, fontWeight: 'bold', textAlign: 'left' }}>
                  {title}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'left' }}>
                {children}
              </Box>
            </CardContent>
          </Grid>
        </Grid>
      </Card>
    </motion.div>
  );
};

export default SectionCard;
